import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { theme } from "../../../theme";
import { StatusType } from "../Explain/types";
import { InputPropsType } from "./InputPropsT";
import { ReactComponent as Check } from "../../../assets/icons/Check.svg";
import { ReactComponent as Cancel } from "../../../assets/icons/Cancel.svg";
import { ReactComponent as Up } from "../../../assets/icons/DropDownUp.svg";
import { ReactComponent as Down } from "../../../assets/icons/DropDownDown.svg";
import Explain from "../Explain";

interface SelectProps extends InputPropsType {
  onChange: (e: any) => void;
  selectArray?: { id: number; value: string | number }[];
  status?: StatusType;
  onExplainClick?: () => void;
  explain?: string;

  isCancelBtn?: true;
}
const SelectBox: React.FC<SelectProps> = ({
  disabled,
  explain,
  value,
  status,
  selectArray,
  placeholder,
  isCancelBtn,
  onChange,
  onExplainClick,
}) => {
  const [isOpen, setOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const eventListener = (e: any) => {
    if (e.target !== selectRef.current) setOpen(false);
  };

  useEffect(() => {
    window.addEventListener("click", eventListener);
  }, []);

  return (
    <>
      <SelectBoxContainer>
        <SelectBoxInput
          ref={selectRef}
          disabled={disabled}
          status={status}
          open={isOpen}
          onBlur={() => setOpen(false)}
          onClick={() => (disabled ? "" : setOpen(!isOpen))}
        >
          <div
            style={{
              overflow: "hidden",
            }}
          >
            {value === undefined || value === ""
              ? <PlaceHolder>{placeholder}</PlaceHolder> || "선택"
              : value || " "}
          </div>
          <SelectBoxButtonWrapper
            isNotCancelBtn={!isCancelBtn}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) setOpen(!isOpen);
            }}
          >
            {isCancelBtn === true && (
              <CancelBtnWrapper
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    value === "선택" ||
                    value === "전체" ||
                    value === undefined ||
                    value === ""
                  ) {
                    setOpen(!isOpen);
                  }
                }}
              >
                <Cancel
                  style={{
                    marginRight: "10px",
                    cursor: "pointer",
                    fill:
                      value !== "선택" &&
                      value !== "전체" &&
                      value !== undefined &&
                      value !== ""
                        ? "black"
                        : "white",
                    transition: "fill 0.3s",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      value === "선택" ||
                      value === "전체" ||
                      value === undefined ||
                      value === ""
                    ) {
                      setOpen(!isOpen);
                    }
                    if (
                      value !== "선택" &&
                      value !== "전체" &&
                      value !== undefined &&
                      value !== ""
                    ) {
                      if (selectArray)
                        onChange(
                          selectArray[0].value === "선택"
                            ? "선택"
                            : selectArray[0].value === "전체"
                            ? "전체"
                            : ""
                        );
                    }
                  }}
                />
              </CancelBtnWrapper>
            )}
            {isOpen ? <Up /> : <Down />}
          </SelectBoxButtonWrapper>
        </SelectBoxInput>

        <SelectBoxList open={isOpen}>
          {selectArray?.map((item) => (
            <SelectOption
              key={item.value}
              current={item.value === value}
              onClick={() => {
                if (!disabled) {
                  onChange(item.value);
                  setOpen(!isOpen);
                }
              }}
            >
              {item.value}
              {item.value === value && <Check />}
            </SelectOption>
          ))}
        </SelectBoxList>
      </SelectBoxContainer>
      {explain && explain.length > 0 && (
        <Explain explain={explain} onClick={onExplainClick} status={status} />
      )}
    </>
  );
};

export default SelectBox;

const SelectBoxContainer = styled.div`
  position: relative;
  user-select: none;
  flex: 1 1 100%;
  transition: all 0.3s;
`;
const boxSlide = keyframes`
  0% {
    opacity:0;
    height:0px;
  }

  100% {
    opacity: 1;
    height:32px;
    transform:translateY(0px)
  }
  `;
const SelectBoxInput = styled.div<{
  disabled?: boolean;
  open: boolean;
  status?: StatusType;
}>`
  transition: all 0.3s;
  border: 1px solid
    ${({ open }) => (open ? theme.colors.mainPrimary3 : theme.colors.gray6)};
  box-shadow: ${({ open }) =>
    open ? " 0 0 0 2px rgb(24 144 255 / 20%)" : "0px"};
  line-height: 32px;
  display: flex;
  justify-content: space-between;
  transition: box-shadow 0.5s, border 0.3s;
  box-sizing: border-box;
  border-radius: 4px;
  padding-left: 12px;
  cursor: pointer;
  padding-right: 12px;
  height: 32px;
  ::placeholder {
    color: ${theme.colors.gray6};
  }
  ${({ disabled }) =>
    disabled &&
    `background:#F8F8F8;
  cursor:not-allowed;
  border: 1px solid #D2D2D2;
  box-sizing: border-box;
  border-radius: 4px;
  color:#6E6E6E;
  &:hover{
      border-color:${theme.colors.gray6};
  }
  `}
  border-color: ${({ status }) => {
    switch (status) {
      case "success":
        return theme.colors.blue2;
      case "error":
        return theme.colors.red3;
      default:
        return theme.colors.gray6;
    }
  }};
`;
const SelectBoxButtonWrapper = styled.div<{ isNotCancelBtn?: boolean }>`
  display: flex;
  transition: all 0.3s;
  justify-content: flex-end;
  width: ${({ isNotCancelBtn }) => (isNotCancelBtn ? "fit-content" : "40px")};
  height: 32px;
  align-items: center;
`;
const SelectBoxList = styled.ul<{ open?: boolean }>`
  display: ${({ open }) => (open ? "block" : "none")};
  overflow-x: hidden;
  transition: all 0.3s;
  overflow-y: auto;
  z-index: 800;
  list-style: none;
  width: 100%;
  background-color: white;
  position: absolute;
  box-shadow: 0px 4px 20px rgba(25, 25, 30, 0.15);
  border-radius: 4px;
  padding: 2px 0px 2px 0px;
  max-height: calc(32px * 6);
`;
const CancelBtnWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: color 0.3s;
  transition: all 0.3s;
`;
const SelectOption = styled.li<{ current?: boolean }>`
  display: flex;
  color: ${({ current }) =>
    current ? theme.colors.mainPrimary3 : theme.colors.black};
  min-width: fit-content;
  max-width: 100%;
  flex-direction: row;
  justify-content: space-between;
  list-style: none;
  height: 32px;
  align-items: center;
  transition: all 0.3s;
  animation: ${boxSlide} 0.3s backwards;
  padding: 0px 12px;
  &:hover {
    background: #f8f8f8;
  }
  cursor: pointer;
`;
const PlaceHolder = styled.span`
  color: ${theme.colors.gray6};
`;
